// Formatting utilities
// For comprehensive date/time formatting, see: /config/formatting/datetime-config.ts
import { formatInTimezone, getTimezoneAbbreviation } from './timezone';

// Re-export datetime formatting from central config
export {
  formatDate as formatDateConfig,
  formatTime as formatTimeConfig,
  formatDateTime as formatDateTimeConfig,
  getRelativeTime
} from '../../../../config/formatting/datetime-config';

// Legacy formatDate for backward compatibility
// Consider migrating to formatDateConfig for locale support
export const formatDate = (date: string | Date): string => {
  return new Intl.DateTimeFormat('en-US').format(new Date(date));
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

export const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  return phone;
};

/**
 * Split a stored performance-preference value into its display parts.
 * The buddy-group preference picker stores values in the form
 * `<name> [<isoDatetime>]` so two same-named performances on different
 * days round-trip as distinct picks; this peels them apart so callers
 * can stack the slot title and the formatted date on two lines instead
 * of wrapping mid-string.
 *
 * Pass the parent rodeo's IANA `timezone` so the datetime renders in
 * that zone with a US-style abbreviation appended (e.g. "Fri, May 15,
 * 6:00 PM PT") — every viewer then sees the same wall-clock the host
 * configured plus an explicit zone hint. When `timezone` is omitted we
 * fall back to the viewer's local zone with no abbreviation (legacy
 * behaviour) so older callers without a timezone reference still work.
 *
 * Returns `{ title, datetime: '' }` for legacy values without a
 * `[...]` suffix or for ones whose suffix doesn't parse.
 */
export const formatPreferenceParts = (
  value: string | null | undefined,
  timezone?: string | null,
): { title: string; datetime: string } => {
  if (!value) return { title: '', datetime: '' };
  const trimmed = value.trim();
  if (!trimmed) return { title: '', datetime: '' };
  const match = trimmed.match(/^(.+?)\s*\[([^\]]+)\]\s*$/);
  if (!match) return { title: trimmed, datetime: '' };
  const head = match[1].trim();
  const iso = match[2].trim();
  if (Number.isNaN(Date.parse(iso))) return { title: head, datetime: '' };
  if (timezone) {
    const datetime = formatInTimezone(
      iso,
      timezone,
      {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      },
      true,
    );
    return { title: head, datetime };
  }
  const datetime = new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return { title: head, datetime };
};

/**
 * Single-line variant of `formatPreferenceParts` for inline contexts
 * (e.g. "Preferences: X → Y" on the rodeo card). Joins the title and
 * datetime with an em-dash; legacy values fall through unchanged.
 *
 * Pass `timezone` to anchor the datetime to the parent rodeo's saved
 * zone with the abbreviation appended; omit it for the legacy behaviour
 * (viewer's local zone, no abbreviation).
 */
export const formatPreferenceLabel = (
  value: string | null | undefined,
  timezone?: string | null,
): string => {
  const { title, datetime } = formatPreferenceParts(value, timezone);
  if (!title) return '';
  if (datetime) return `${title} — ${datetime}`;
  // No datetime suffix on the value itself, but if we have a timezone
  // and the title is a non-time slot like "Slack" / "Draw Out", we
  // still leave it bare — there's no time to qualify.
  return title;
};

// Re-export so callers that already deal with preference labels can
// reach for the timezone abbreviation without a second import line.
export { getTimezoneAbbreviation };
